import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

type Props = {
  formik: any;
};

export default function AdditionalDetails({ formik }: Props) {
  const [additionalInfoRows, setAdditionalInfoRows] = useState([
    { id: 1, text: "", checked: true },
  ]);
  const [notesInitialized, setNotesInitialized] = useState(false);

  useEffect(() => {
    const notes = additionalInfoRows
      .filter((row) => row.text.trim() !== "")
      .map((row) => ({
        note: row.text,
        sendToInspector: row.checked,
      }));

    const currentNotes = formik.values.notes || [];
    const notesChanged = JSON.stringify(notes) !== JSON.stringify(currentNotes);

    if (notesChanged) {
      formik.setFieldValue("notes", notes);
    }
  }, [additionalInfoRows]); // eslint-disable-line

  useEffect(() => {
    if (formik.values.notes && formik.values.notes.length === 0 && notesInitialized) {
      setNotesInitialized(false);
    }
  }, [formik.values.notes, notesInitialized]); // eslint-disable-line

  useEffect(() => {
    if (!notesInitialized && formik.values.notes && formik.values.notes.length > 0) {
      const existingNotes = formik.values.notes.map((note: any, index: number) => ({
        id: index + 1,
        text: note.note || "",
        checked: note.sendToInspector ?? false,
      }));
      setAdditionalInfoRows(existingNotes);
      setNotesInitialized(true);
    }
  }, [formik.values.notes, notesInitialized]); // eslint-disable-line

  useEffect(() => {
    if (formik.values.notes && formik.values.notes.length === 0) {
      setAdditionalInfoRows([{ id: 1, text: "", checked: true }]);
    }
  }, [formik.values.notes]); // eslint-disable-line

  const addNewRow = () => {
    setAdditionalInfoRows((prev) => [
      ...prev,
      { id: Date.now(), text: "", checked: false },
    ]);
  };

  const deleteRow = (id: number) => {
    if (additionalInfoRows.length > 1) {
      setAdditionalInfoRows(additionalInfoRows.filter((row) => row.id !== id));
    }
  };

  const handleCheckboxChange = (id: number, checked: boolean) => {
    setAdditionalInfoRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, checked } : row))
    );
  };

  const handleTextChange = (id: number, text: string) => {
    setAdditionalInfoRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, text } : row))
    );
  };

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        backgroundColor: "#FAFAFA",
        border: "1px solid #E0E0E0",
        borderRadius: "8px",
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 400, mb: 2, fontSize: "24px" }}>
        Order Notes
      </Typography>
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            pb: 1,
            mb: 1,
            alignItems: "center",
            flexWrap: "nowrap",
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
              Note
            </Typography>
          </Box>
          <Box sx={{ width: 40, flexShrink: 0 }} />
          <Box sx={{ minWidth: 140, flexShrink: 0, textAlign: "center" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
              Inspector Viewable?
            </Typography>
          </Box>
        </Box>
        {additionalInfoRows.map((row) => (
          <Box
            key={row.id}
            sx={{
              display: "flex",
              alignItems: "center",
              py: 1,
              flexWrap: "nowrap",
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <textarea
                placeholder=""
                value={row.text}
                onChange={(e) => handleTextChange(row.id, e.target.value)}
                disabled={!formik.values.clientId}
                style={{
                  width: "100%",
                  minHeight: 40,
                  padding: 12,
                  border: "1px solid #E0E0E0",
                  borderRadius: 4,
                  resize: "vertical",
                  fontFamily: "inherit",
                  fontSize: 14,
                  boxSizing: "border-box",
                }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              <Button
                size="small"
                onClick={() => deleteRow(row.id)}
                disabled={!formik.values.clientId || additionalInfoRows.length <= 1}
                sx={{ minWidth: "auto", p: 0.5, color: "error.main" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </Button>
            </Box>
            <Box sx={{ minWidth: 140, flexShrink: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Checkbox
                checked={row.checked}
                onChange={(e) => handleCheckboxChange(row.id, e.target.checked)}
                disabled={!formik.values.clientId}
                size="small"
                sx={{ color: "primary.main", "&.Mui-checked": { color: "primary.main" } }}
              />
            </Box>
          </Box>
        ))}
        <Button
          variant="text"
          startIcon={<AddOutlinedIcon />}
          onClick={addNewRow}
          sx={{ color: "primary.main", textTransform: "none", p: 0, alignSelf: "flex-start", mt: 1, fontSize: "13px", fontWeight: 500 }}
          disabled={!formik.values.clientId}
        >
          ADD ORDER NOTE
        </Button>
      </Box>
    </Box>
  );
}
